import { ipfsToHttp } from "@/lib/image-upload";
import { createFileRoute, ClientOnly, Link } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useAccount } from "wagmi";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatEther } from "viem";
import { ExternalLink, Wallet } from "lucide-react";
import { networks } from "@/lib/wallet";

type FilterKind = "all" | "mint" | "list" | "buy";
type SortDir = "newest" | "oldest";

export const Route = createFileRoute("/activity")({
  component: () => (<ClientOnly fallback={null}><ActivityPage /></ClientOnly>),
  head: () => ({
    meta: [
      { title: "My Activity - RobotDex" },
      { name: "description", content: "Your minting, listing, and purchase history on RobotDex." },
    ],
  }),
});

// Build explorer map from the configured wagmi networks so it stays in sync
// with the chains the wallet can actually connect to.
const CHAIN_META: Record<number, { explorer: string | null; symbol: string; name: string }> =
  Object.fromEntries(
    networks.map((n: any) => [
      Number(n.id),
      {
        explorer:
          n.blockExplorers?.default?.url?.replace(/\/+$/, "") ??
          n.blockExplorers?.etherscan?.url?.replace(/\/+$/, "") ??
          null,
        symbol: n.nativeCurrency?.symbol ?? "ETH",
        name: n.name ?? `Chain ${n.id}`,
      },
    ]),
  );

function short(a?: string | null) {
  return a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "-";
}

function explorerUrl(chainId?: number | null, kind: "tx" | "address" | "token" = "tx", value?: string | null) {
  if (!chainId || !value) return null;
  const base = CHAIN_META[chainId]?.explorer;
  return base ? `${base}/${kind}/${value}` : null;
}

const KIND_LABEL: Record<string, string> = {
  mint: "Mint",
  mint_request: "Mint (pending)",
  list: "Listing",
  buy: "Purchase",
};

function ActivityPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { address, isConnected } = useAccount();

  if (!mounted) return <div className="p-8 text-muted-foreground">Loading…</div>;

  if (!isConnected || !address) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center">
        <Wallet className="h-10 w-10 mx-auto text-primary" />
        <h1 className="mt-4 text-2xl font-bold neon-text">Connect your wallet</h1>
        <p className="text-muted-foreground mt-2">Sign in with your wallet to see your minting, listing, and purchase history.</p>
        <div className="mt-6"><Button asChild variant="outline"><Link to="/">Back to home</Link></Button></div>
      </div>
    );
  }

  return <UserActivity address={address.toLowerCase()} />;
}

const PAGE_SIZE = 25;

function UserActivity({ address }: { address: string }) {
  const [filter, setFilter] = useState<FilterKind>("all");
  const [sort, setSort] = useState<SortDir>("newest");

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["activity-mine", address, filter, sort],
    initialPageParam: 0,
    getNextPageParam: (last: any[], all) =>
      last.length < PAGE_SIZE ? undefined : all.length,
    queryFn: async ({ pageParam }) => {
      const from = (pageParam as number) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let q = supabase
        .from("activities")
        .select("*, nft_drops(id, name, symbol, logo_url)")
        .eq("actor_wallet", address);
      if (filter === "mint") q = q.in("kind", ["mint", "mint_request"]);
      else if (filter === "list") q = q.eq("kind", "list");
      else if (filter === "buy") q = q.eq("kind", "buy");
      q = q.order("created_at", { ascending: sort === "oldest" }).range(from, to);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 60_000,
    staleTime: 10_000,
  });

  const rows = (data?.pages ?? []).flat();

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
      <PageHeader
        title="My Activity"
        subtitle={`History for ${short(address)}`}
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterKind)}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="mint">Mints</TabsTrigger>
            <TabsTrigger value="list">Listings</TabsTrigger>
            <TabsTrigger value="buy">Purchases</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={sort} onValueChange={(v) => setSort(v as SortDir)}>
          <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
          </SelectContent>
        </Select>
      </div>


      {isLoading && <SkeletonList />}

      {isError && (
        <Card className="p-6 bg-card/60 neon-border text-center">
          <p className="text-destructive">Failed to load activity.</p>
          <p className="text-xs text-muted-foreground mt-1">{String((error as any)?.message ?? error)}</p>
          <Button onClick={() => refetch()} className="mt-4">Try again</Button>
        </Card>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <Card className="p-10 text-center neon-border bg-card/60">
          <p className="text-muted-foreground">No activity yet. Mint or list an NFT to see it here.</p>
          <div className="mt-4 flex justify-center gap-2">
            <Button asChild><Link to="/market">Browse market</Link></Button>
            <Button asChild variant="outline"><Link to="/create">Create a drop</Link></Button>
          </div>
        </Card>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <Card className="bg-card/60 neon-border overflow-hidden">
            <div className="divide-y divide-border/60">
              {rows.map((a: any) => {
                const meta = a.chain_id ? CHAIN_META[a.chain_id] : undefined;
                const url = explorerUrl(a.chain_id, "tx", a.tx_hash);
                const symbol = meta?.symbol ?? "ETH";
                const price =
                  a.price_wei && a.price_wei !== "0"
                    ? `${Number(formatEther(BigInt(a.price_wei))).toFixed(4)} ${symbol}`
                    : null;
                return (
                  <div key={a.id} className="p-4 flex items-center gap-4 hover:bg-accent/30">
                    <div className="h-10 w-10 rounded overflow-hidden bg-primary/10 shrink-0 flex items-center justify-center">
                      {a.nft_drops?.logo_url ? (
                        <img src={ipfsToHttp(a.nft_drops.logo_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold neon-text">{a.nft_drops?.symbol?.slice(0, 3) ?? "-"}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="neon-border uppercase text-[10px] tracking-wider">
                          {KIND_LABEL[a.kind] ?? a.kind.replace("_", " ")}
                        </Badge>
                        {a.nft_drops?.id ? (
                          <Link to="/drop/$id" params={{ id: a.nft_drops.id }} className="text-sm font-semibold hover:text-primary truncate">
                            {a.nft_drops?.name ?? "-"}
                          </Link>
                        ) : (
                          <span className="text-sm font-semibold truncate">{a.nft_drops?.name ?? "-"}</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{meta?.name ?? `Chain ${a.chain_id ?? "-"}`}</span>
                        {price && <span className="text-primary">· {price}</span>}
                        {url ? (
                          <a href={url} target="_blank" rel="noreferrer" className="hover:text-primary inline-flex items-center gap-1">
                            · {a.tx_hash.slice(0, 10)}…<ExternalLink className="h-3 w-3" />
                          </a>
                        ) : a.tx_hash ? (
                          <span>· {a.tx_hash.slice(0, 10)}…</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap text-right">
                      <div>{new Date(a.created_at).toLocaleDateString()}</div>
                      <div>{new Date(a.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
          <div className="mt-4 flex items-center justify-center gap-3 text-xs text-muted-foreground">
            <span>Showing {rows.length} event{rows.length === 1 ? "" : "s"}</span>
            {hasNextPage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SkeletonList() {
  return (
    <Card className="bg-card/60 neon-border overflow-hidden">
      <div className="divide-y divide-border/60">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
            <div className="h-10 w-10 rounded bg-muted/40" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-40 bg-muted/40 rounded" />
              <div className="h-2 w-24 bg-muted/30 rounded" />
            </div>
            <div className="h-3 w-16 bg-muted/30 rounded" />
          </div>
        ))}
      </div>
    </Card>
  );
}
